import apiClient from '../index';
import type { CanonicalRowFilter } from '../../schemas';
import type { ModuleCode } from '../../features/modules/modules';

/** The catalogue of one module (absent = the server's default, O2C). */
export const getAnalysisCatalogue = async (module?: ModuleCode) => {
	const response = await apiClient.get('/analyses', {
		params: module ? { module } : undefined,
	});
	return response.data;
};

export const getLineage = async (path: string) => {
	const response = await apiClient.get(path);
	return response.data;
};

/** The audit's analyses, one row each, whichever engine computes them. */
export const getAnalysisOverview = async (auditId: string) => {
	const response = await apiClient.get(
		`/audits/${auditId}/analysis-overview`,
	);
	return response.data;
};

/** One analysis's own page: the row, its note, and (admin only) technical
 *  details. */
export const getAnalysisDetail = async (auditId: string, slug: string) => {
	const response = await apiClient.get(`/audits/${auditId}/analyses/${slug}`);
	return response.data;
};

/** Run every chosen analysis, or just the named ones. */
export const runAnalyses = async (auditId: string, slugs?: string[]) => {
	const response = await apiClient.post(`/audits/${auditId}/analyses/run`, {
		slugs,
	});
	return response.data;
};

export const retryFailedAnalyses = async (auditId: string) => {
	const response = await apiClient.post(
		`/audits/${auditId}/analyses/retry-failed`,
	);
	return response.data;
};

export interface ResultQuery {
	part: 'result' | 'exceptions' | 'supporting';
	page: number;
	pageSize: number;
	q?: string;
	column?: string;
}

/** One page of one analysis's result (exceptions or supporting rows). */
export const getAnalysisResult = async (
	auditId: string,
	slug: string,
	query: ResultQuery,
) => {
	const response = await apiClient.get(
		`/audits/${auditId}/analyses/${slug}/result`,
		{
			params: {
				part: query.part,
				page: query.page,
				pageSize: query.pageSize,
				/* The wire param is `query`, not `q` — `q` is this module's own field
         name for the search box's value. */
				query: query.q || undefined,
				column: query.column || undefined,
			},
		},
	);
	return response.data;
};

/** The wizard's "Choose analyses" step: Ready/Blocked for the whole catalogue,
 *  for one client and data-scope filter — before any audit exists. */
export const getAnalysisAvailability = async (
	clientId: string,
	module: ModuleCode,
	filter: CanonicalRowFilter,
) => {
	const response = await apiClient.post('/audits/analysis-availability', {
		clientId,
		module,
		filter,
	});
	return response.data;
};

/** "Edit analyses": Ready/Blocked for the whole catalogue against an existing
 *  audit's frozen scope. */
export const getAuditAnalysisAvailability = async (auditId: string) => {
	const response = await apiClient.get(
		`/audits/${auditId}/analysis-availability`,
	);
	return response.data;
};
